import { modifier, tablet } from '@styles/mixins'
import { colors, grid } from '@styles/vars'
import React, { useEffect, useState } from 'react'
import styled, { css } from 'styled-components'
import Button from './Button'
import { ReactComponent as ChevronIcon } from '@icons/chevron.svg'
import Grid from './Grid'
import Flex from './Flex'
import { useSidepanelContext, useSidepanelStateContext } from '@contexts/sidepanel'

export default function Sidepanel({ heading, children, options, maxWidth }: Props) {
    const [visible, setVisible] = useState(false)

    const state = useSidepanelStateContext()
    const sidepanel = useSidepanelContext()

    useEffect(() => {
        switch (state) {
            case "visible":
                setTimeout(() => setVisible(true)) // setTimeout allows smooth animation
                break
            case "hiding":
                setVisible(false)
                break
            default:
                break
        }
    }, [state])
    
    const { back: BackButton } = options || {}

    return (
        <Wrapper visible={visible}>
            <Background onClick={sidepanel.close} />
            <Panel maxWidth={maxWidth}>
                <PanelContainer>
                    <Grid gap="30px">
                        <Header>
                            <Flex gap="20px" alignItems="center">
                                {BackButton 
                                    ? <BackButton close={sidepanel.close} />
                                    : <DefaultBackButton close={sidepanel.close} />}
                                {heading && <h2 className="h3">{heading}</h2>}
                            </Flex>
                        </Header>
                        {children}
                    </Grid>
                </PanelContainer>
            </Panel>
        </Wrapper>
    )
}

// COMPONENTS
const DefaultBackButton = ({ close }: BackButtonProps) => (
    <Flex>
        <Button type="tertiary" scheme="outline" onClick={close} icon={<ChevronStyledIcon />}>
            Back
        </Button>
    </Flex>
)

// STYLES
const ChevronStyledIcon = styled(ChevronIcon)`
    display: block;
    transform: rotate(90deg);
`

const Background = styled.div`
    position: absolute;
    z-index: 1;
    height: 100%;
    width: 100%;
    background: ${colors.black};
    opacity: 0;
    transition: .2s ease-out;
    transition-delay: .2s;
`

const Panel = styled.div<{ maxWidth?: string }>`
    position: absolute;
    z-index: 2;
    top: 0;
    right: -90%;
    height: 100vh;
    background: #fff;
    width: 90%;
    transition: .2s ease-out;
    overflow: auto;

    ${tablet(css<{ maxWidth?: string }>`
        width: 50%;
        right: -50%;
        max-width: ${props => props.maxWidth || "none"};
    `)}
`

const PanelContainer = styled.div`
    padding: ${grid.margins.phone};

    ${tablet(css`
        padding: ${grid.margins.tablet};
    `)}
`

const Header = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    background: #fff;
    border-bottom: 1px solid ${colors.border};
    margin-top: -${grid.margins.phone};
    margin-left: -${grid.margins.phone};
    margin-right: -${grid.margins.phone};
    padding: ${grid.margins.phone};

    ${tablet(css`
        margin-top: -${grid.margins.tablet};
        margin-left: -${grid.margins.tablet};
        margin-right: -${grid.margins.tablet};
        padding: ${grid.margins.phone} ${grid.margins.tablet};
    `)}
`

const Wrapper = styled.div<{ visible: boolean }>`
    position: fixed;
    top: 0;
    left: 100%;
    z-index: 99;
    height: 100vh;
    width: 100%;
    transition-delay: .4s;

    ${modifier("visible", css`
        left: 0;
        transition-delay: unset;

        ${Background} {
            opacity: .1;
            transition-delay: unset;
        }

        ${Panel} {
            right: 0;
            transition-delay: .2s;
        }
    `)}
`

// TYPES
interface Props {
    children: React.ReactNode
    heading?: string
    description?: React.ReactNode
    maxWidth?: string
    options?: {
        back?: React.ComponentType<BackButtonProps>
    }
}

export interface BackButtonProps {
    close(): void
}